import React from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import type { AdminRulesBaseRow, AdminRulesColumnDef } from "../types";
import { RulesHierarchicalFilters } from "./RulesHierarchicalFilters";
import type { RulesHierarchicalFilterState } from "./RulesHierarchicalFilters";
import {
  ActionsCell,
  ActiveToggleCell,
  EventSubTypeCell,
  EventTypeCell,
  TenantCell,
} from "./columns/StandardRulesColumnCells";

interface RulesDataTableProps<TRow extends AdminRulesBaseRow> {
  rows: TRow[];
  loading?: boolean;
  filters: RulesHierarchicalFilterState;
  onFiltersChange: (next: RulesHierarchicalFilterState) => void;
  customColumns: AdminRulesColumnDef<TRow>[];
  onEdit: (row: TRow) => void;
  onToggleActive: (row: TRow) => void;
  mutationsDisabled?: boolean;
}

export const RulesDataTable = <TRow extends AdminRulesBaseRow>({
  rows,
  loading,
  filters,
  onFiltersChange,
  customColumns,
  onEdit,
  onToggleActive,
  mutationsDisabled,
}: RulesDataTableProps<TRow>) => {
  const tableHeader = (
    <RulesHierarchicalFilters filters={filters} onChange={onFiltersChange} />
  );

  return (
    <DataTable
      value={rows}
      loading={loading}
      header={tableHeader}
      dataKey="id"
      paginator
      rows={10}
      rowsPerPageOptions={[5, 10, 25, 50]}
      stripedRows
      emptyMessage="Sin registros"
      tableStyle={{ minWidth: "60rem" }}
    >
      <Column
        header="Tipo de evento"
        body={(row: TRow) => EventTypeCell(row)}
        sortable
        sortField="eventTypeName"
      />
      <Column
        header="Subtipo"
        body={(row: TRow) => EventSubTypeCell(row)}
        sortable
        sortField="eventSubTypeName"
      />
      <Column
        header="Tenant"
        body={(row: TRow) => TenantCell(row)}
        sortable
        sortField="tenantName"
      />
      {customColumns.map((column) => (
        <Column
          key={column.field}
          field={column.field}
          header={column.header}
          body={column.body ? (row: TRow) => column.body?.(row) : undefined}
          sortable={column.sortable}
          style={column.style}
        />
      ))}
      <Column
        header="Estado"
        body={(row: TRow) => (
          <ActiveToggleCell row={row} onToggle={onToggleActive} disabled={mutationsDisabled} />
        )}
      />
      <Column
        header="Acciones"
        body={(row: TRow) => (
          <ActionsCell row={row} onEdit={onEdit} disabled={mutationsDisabled} />
        )}
      />
    </DataTable>
  );
};
